import find from "lodash/find";
import React, { FunctionComponent } from "react";
import { FilterTag } from "../../../../agreed/filters";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { Unit } from "../../../../types/customization/box";
import { ContentPage, Cropping } from "../../../../types/customization/content";
import { Template, TemplatePageDefinition } from "../../../../types/customization/template";
import { SelectionPhoto } from "../../../../types/selection/selection";
import { sizeDmmToMm, sizeMmToPx } from "../../../../utils/customization/box";
import { calcDefaultCropping } from "../../../../utils/customization/cropping";
import { findEditablePictureDefinition } from "../../../../utils/customization/finders";
import { findPhoto } from "../../../../utils/selection/finders";
import { imageUrlFromSelectionPhoto } from "../../../../utils/selection/photo";
import EditablePictureSvg from "../../../atoms/customization/EditablePictureSvg";
import PreviewImage from "./preview.jpg";
import "./style.scss";

interface Props {
  elementDefinitionKey: string;
  page: ContentPage;
  pageDefinition: TemplatePageDefinition;
  ppmm: number;
  template: Template;
}

const EditablePicturePreview: FunctionComponent<Props> = ({
  elementDefinitionKey,
  page,
  pageDefinition,
  ppmm,
  template,
}) => {
  const { selection } = useCustomizationContext();

  const elementDefinition = findEditablePictureDefinition(elementDefinitionKey, pageDefinition);

  const elementSizeMm = sizeDmmToMm(elementDefinition);
  const elementSizePx = sizeMmToPx(elementSizeMm, ppmm);

  const element = find(
    page.editablePictures,
    ({ editablePictureKey }) => editablePictureKey === elementDefinition.key
  );
  let elementCropping: Cropping;
  let elementFilterTag: FilterTag = "none";
  let selectionPhoto: SelectionPhoto;

  const ratio = elementSizeMm.width.value / elementSizeMm.height.value;

  if (!element) {
    if (page.editablePictures.length > 0) {
      const firstEditablePicture = page.editablePictures[0];
      selectionPhoto = findPhoto(firstEditablePicture.selectionPhotoKey, selection);
    } else {
      selectionPhoto = {
        key: "preview",

        width: { unit: Unit.PX, value: 500 },
        height: { unit: Unit.PX, value: 667 },

        provider: "upload",
        providerRef: "upload-preview",
        providerThumbnailUrl: PreviewImage,
        providerPictureUrl: PreviewImage,
      };
    }
    const cropping = calcDefaultCropping(
      {
        nextRatio: ratio,
        selectionPhoto,
      },
      !elementDefinition.shapeKey
    );
    elementCropping = cropping;
  } else {
    selectionPhoto = findPhoto(element.selectionPhotoKey, selection);
    const cropping = calcDefaultCropping(
      {
        nextRatio: ratio,
        selectionPhoto,
      },
      !elementDefinition.shapeKey
    );
    elementCropping = cropping;
    elementFilterTag = element.filterTag;
  }

  let shapeSvgContent: string | undefined;
  if (elementDefinition.shapeKey) {
    const shapeDefinition = find(
      template.shapeDefinitions,
      ({ key }) => key === elementDefinition.shapeKey
    );
    if (!shapeDefinition) {
      return <div className="EditablePicturePreview">Error! ShapeDefinition not found.</div>;
    }

    shapeSvgContent = shapeDefinition.svgContent;
  }

  const imageUrl = imageUrlFromSelectionPhoto(selectionPhoto, "full");

  return (
    <div className="EditablePicturePreview">
      <EditablePictureSvg
        cropping={elementCropping}
        filterTag={elementFilterTag}
        heightPx={elementSizePx.height.value}
        imageUrl={imageUrl}
        shapeSvgContent={shapeSvgContent}
        widthPx={elementSizePx.width.value}
      />
    </div>
  );
};

export default EditablePicturePreview;
